/**
 * Vista de plataforma — agregados de coste por Data Region (Sprint 286 I;
 * REQ-MINT-013; ADR-131 §3).
 *
 * Los hechos llegan de la consola M2M del CP (los DR aplican y auditan); el
 * payload **no incluye identificadores de tenant** (SEC-022) y aquí tampoco se
 * muestran: solo región, modelos, componentes, fuente y fechas. Si la consola
 * del CP no está enroutada, la vista degrada a un aviso explícito (sin dato).
 */

import { useMemo } from 'react';
import { Activity, Server, ShieldCheck } from 'lucide-react';
import { Button, Card, CardContent, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import {
  useModelIntelPlatformCostFacts,
  useModelIntelUsageOverview,
} from '../hooks/useModelIntel';
import { summarizePlatformFacts } from '../lib/governance';
import { formatDate, formatUsd } from '../lib/pricing';

export default function SystemPricingOverviewPage() {
  const { t } = useI18n();
  const facts = useModelIntelPlatformCostFacts();
  const usage = useModelIntelUsageOverview({ periodType: 'day', limit: 200 });
  const summary = useMemo(() => summarizePlatformFacts(facts.data), [facts.data]);
  const usageRows = useMemo(() => {
    const grouped = new Map<
      string,
      {
        method: string;
        calls: number;
        tokens: number;
        hitl: number;
        tools: number;
        cost: number;
        costVisible: boolean;
        coverageWeighted: number;
        coverageCalls: number;
      }
    >();
    for (const cell of usage.data?.items ?? []) {
      const method = cell.usage_method ?? 'desconocido';
      const row =
        grouped.get(method) ??
        {
          method,
          calls: 0,
          tokens: 0,
          hitl: 0,
          tools: 0,
          cost: 0,
          costVisible: true,
          coverageWeighted: 0,
          coverageCalls: 0,
        };
      const calls = cell.tasks_count ?? 0;
      row.calls += calls;
      row.tokens += (cell.tokens_in ?? 0) + (cell.tokens_out ?? 0);
      row.hitl += cell.hitl_interventions ?? 0;
      row.tools += cell.tool_calls_count ?? 0;
      row.cost += cell.cost ?? 0;
      row.coverageWeighted += (cell.coverage ?? 0) * calls;
      row.coverageCalls += calls;
      row.costVisible = row.costVisible && cell.cost_visible !== false;
      grouped.set(method, row);
    }
    return [...grouped.values()].sort((a, b) => b.tokens - a.tokens);
  }, [usage.data]);
  const showCost = usageRows.some((row) => row.costVisible);

  return (
    <div className="space-y-6 p-6" data-testid="system-model-pricing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-semibold">{t('modelIntel.platform.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('modelIntel.platform.subtitle')}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => facts.refetch()}
          disabled={facts.isFetching}
          data-testid="platform-refresh"
        >
          {t('modelIntel.pricing.refresh')}
        </Button>
      </div>

      <p
        className="rounded-md border border-border p-2 text-xs text-gray-500 dark:text-gray-400"
        data-testid="platform-no-tenants"
      >
        {t('modelIntel.platform.noTenantsNote')}
      </p>

      {facts.isLoading && (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      )}

      {facts.isError && !facts.isLoading && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
          role="status"
          data-testid="platform-unavailable"
        >
          {t('modelIntel.platform.unavailable')}
        </div>
      )}

      {facts.data && (
        <>
          {summary.regions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="platform-empty">
              {t('modelIntel.platform.empty')}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('modelIntel.platform.cards.regions')}
                    </div>
                    <div className="text-xl font-semibold tabular-nums" data-testid="platform-regions-count">
                      {summary.regions.length}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('modelIntel.platform.cards.versions')}
                    </div>
                    <div className="text-xl font-semibold tabular-nums" data-testid="platform-versions-count">
                      {summary.totalVersions}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('modelIntel.platform.cards.okRegions')}
                    </div>
                    <div className="text-xl font-semibold tabular-nums">{summary.okRegions}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('modelIntel.platform.cards.failedRegions')}
                    </div>
                    <div
                      className={`text-xl font-semibold tabular-nums ${
                        summary.failedRegions > 0 ? 'text-red-600 dark:text-red-400' : ''
                      }`}
                      data-testid="platform-failed-count"
                    >
                      {summary.failedRegions}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                    <Server className="h-4 w-4" aria-hidden="true" />
                    {t('modelIntel.platform.regionsTitle')}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="platform-regions">
                      <thead>
                        <tr className="border-b text-left text-xs text-gray-500 dark:text-gray-400">
                          <th className="py-2 pr-3">{t('modelIntel.platform.colRegion')}</th>
                          <th className="py-2 pr-3">{t('modelIntel.platform.colStatus')}</th>
                          <th className="py-2 pr-3">{t('modelIntel.platform.colVersions')}</th>
                          <th className="py-2 pr-3">{t('modelIntel.platform.colModels')}</th>
                          <th className="py-2 pr-3">{t('modelIntel.platform.colLatest')}</th>
                          <th className="py-2">{t('modelIntel.platform.colSources')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.regions.map((region) => (
                          <tr
                            key={region.region}
                            className="border-b last:border-0"
                            data-testid={`platform-region-${region.region}`}
                          >
                            <td className="py-2 pr-3 font-medium">{region.region}</td>
                            <td className="py-2 pr-3">
                              {region.ok ? (
                                <span className="text-green-600 dark:text-green-400">
                                  {t('modelIntel.platform.statusOk')}
                                </span>
                              ) : (
                                <span
                                  className="text-red-600 dark:text-red-400"
                                  title={region.error}
                                >
                                  {t('modelIntel.platform.statusError')}
                                </span>
                              )}
                            </td>
                            <td className="py-2 pr-3 tabular-nums">{region.versions}</td>
                            <td className="py-2 pr-3 tabular-nums">{region.models}</td>
                            <td className="py-2 pr-3">{formatDate(region.latest)}</td>
                            <td className="py-2">
                              <span className="flex flex-wrap gap-1">
                                {region.sourceKinds.map((kind) => (
                                  <span
                                    key={kind}
                                    className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800"
                                  >
                                    {kind}
                                  </span>
                                ))}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

            </>
          )}
        </>
      )}
      {/* V-61 — uso anónimo agregado por forma de consumo (entrega 4) */}
      <Card data-testid="platform-usage-methods">
        <CardContent className="p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium">
            <Activity className="h-4 w-4" aria-hidden="true" />
            {t('modelIntel.platform.usageTitle')}
          </div>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            {t('modelIntel.platform.usageNote')}
          </p>
          {usage.isLoading ? (
            <div className="flex justify-center p-6">
              <Spinner />
            </div>
          ) : usage.isError ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('modelIntel.platform.usageUnavailable')}
            </p>
          ) : usageRows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="platform-usage-empty">
              {t('modelIntel.platform.usageEmpty')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="platform-usage-methods-table">
                <thead>
                  <tr className="border-b text-left text-xs text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-3">{t('modelIntel.platform.colMethod')}</th>
                    <th className="py-2 pr-3 text-right">{t('modelIntel.platform.colCalls')}</th>
                    <th className="py-2 pr-3 text-right">{t('modelIntel.platform.colTokens')}</th>
                    <th className="py-2 pr-3 text-right">{t('modelIntel.platform.colHitl')}</th>
                    <th className="py-2 pr-3 text-right">{t('modelIntel.platform.colTools')}</th>
                    {showCost && (
                      <th className="py-2 text-right">{t('modelIntel.platform.colCost')}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {usageRows.map((row) => (
                    <tr
                      key={row.method}
                      className="border-b last:border-0"
                      data-testid={`platform-usage-method-${row.method}`}
                    >
                      <td className="py-2 pr-3 font-medium">
                        {t(`modelIntel.platform.methods.${row.method}`)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.calls.toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.tokens.toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.hitl.toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {row.tools.toLocaleString()}
                      </td>
                      {showCost && (
                        <td className="py-2 text-right tabular-nums">
                          {row.coverageCalls > 0 &&
                          row.coverageWeighted / row.coverageCalls > 0 ? (
                            <span
                              title={t('modelIntel.platform.coverage', {
                                value: Math.round(
                                  (row.coverageWeighted / row.coverageCalls) * 100,
                                ),
                              })}
                            >
                              {formatUsd(row.cost)}
                            </span>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">
                              {t('modelIntel.platform.noData')}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
